import moment from "moment";
import * as yup from "yup";

export const rolesWithLocationsShifts = [
  "Location Manager",
  "Supervisor",
  "Supervisor Assistant",
  "Operator",
  "Driver",
  "Key-holder",
  "Cameraman",
];

export const operatorRoles = [
  "Zone Manager",
  "Location Manager",
  "Supervisor",
  "Supervisor Assistant",
  "Operator",
  "Driver",
  "Key-holder",
  "Cameraman",
];

export const noDashboardAccessRoles = [
  "Operator",
  "Driver",
  "Key-holder",
  "Cameraman",
];

const requiredForZoneManagerCity = <T extends yup.Schema>(schema: T) =>
  schema.when(["roleType"], {
    is: (roleType: string) => roleType === "Zone Manager",
    then: (s) => s.required("Required"),
    otherwise: (s) => s.notRequired(),
  });

export const schema = yup.object().shape({
  name: yup.string().required("Required"),
  arabicName: yup.string().required("Required"),
  email: yup.string().email("Invalid email").required("Required"),
  nationalId: yup.string(),
  nationality: yup.string().required("Required"),
  dateOfBirth: yup
    .date()
    .nullable()
    .max(new Date(), "Date of birth can’t be in the future"),
  startDate: yup.date().nullable(),
  endDate: yup
    .date()
    .nullable()
    .min(yup.ref("startDate"), "End date must be after joining date"),
  phone: yup.string().required("Required"),
  phone2: yup.string(),
  employeeType: yup.string().required("Required"),
  roleType: yup.string().required("Required"),
  modeType: yup
    .string()
    .required("Required")
    .oneOf(["regular", "support"] as const),
  areaType: yup
    .string()
    .required("Required")
    .oneOf(["location", "city"] as const),
  locations: yup.array().of(
    yup.object().shape({
      locationName: yup.string().required("Required"),
      locationId: yup.string().required("Required"),

      shifts: yup
        .array()
        .of(
          yup.object().shape({
            shiftType: yup.string(),
            startTime: yup
              .date()
              .nullable()
              .typeError("Required")
              .test("required-if-regular", "Required", function (value) {
                const root = this.from?.[this.from.length - 1]?.value;

                return rolesWithLocationsShifts.includes(root?.roleType) &&
                  root?.modeType === "regular"
                  ? !!value
                  : true;
              }),
            endTime: yup
              .date()
              .nullable()
              .typeError("Required")
              .test("required-if-regular", "Required", function (value) {
                const root = this.from?.[this.from.length - 1]?.value;

                return rolesWithLocationsShifts.includes(root?.roleType) &&
                  root?.modeType === "regular"
                  ? !!value
                  : true;
              }),
            days: yup
              .array()
              .of(yup.string().required("Required"))
              .test(
                "required-if-regular",
                "At least one day is required",
                function (value) {
                  const root = this.from?.[this.from.length - 1]?.value;

                  return rolesWithLocationsShifts.includes(root?.roleType) &&
                    root?.modeType === "regular"
                    ? (value?.length ?? 0) > 0
                    : true;
                },
              ),
            startDate: yup
              .date()
              .nullable()
              .typeError("Required")
              .test("required-if-regular", "Required", function (value) {
                const root = this.from?.[this.from.length - 1]?.value;

                return rolesWithLocationsShifts.includes(root?.roleType) &&
                  root?.modeType === "regular"
                  ? !!value
                  : true;
              }),
            endDate: yup
              .date()
              .nullable()
              .typeError("Required")
              .test("required-if-regular", "Required", function (value) {
                const root = this.from?.[this.from.length - 1]?.value;

                return rolesWithLocationsShifts.includes(root?.roleType) &&
                  root?.modeType === "regular"
                  ? !!value
                  : true;
              })
              .test(
                "end-after-start",
                "End date must be after or equal to start date",
                function (value) {
                  const { startDate } = this.parent;

                  // Don't validate comparison when either is empty
                  if (!startDate || !value) return true;

                  return new Date(value) >= new Date(startDate);
                },
              ),
            flexibleHours: yup
              .string()
              .test("max-flexible-hours", function (value) {
                const root = this.from?.[this.from.length - 1]?.value;
                const roleType = root?.roleType;

                if (!value) return true;

                const hours = Number(value);

                if (hours < 0) {
                  return this.createError({
                    message: "Min is 0",
                  });
                }

                if (
                  (roleType === "Location Manager" ||
                    roleType === "Zone Manager") &&
                  hours > 3
                ) {
                  return this.createError({
                    message: "Max flexible hours is 3",
                  });
                }

                if (roleType === "Supervisor" && hours > 2) {
                  return this.createError({
                    message: "Max flexible hours is 2",
                  });
                }

                return true;
              }),
          }),
        )
        .required(),
    }),
  ),

  locationsServices: yup.array().of(
    yup.object().shape({
      // locationName: yup.string().required("Required"),
      locationId: yup
        .string()
        .test("required-if-operator", "Required", function (value) {
          const root = this.from?.[this.from.length - 1]?.value;

          return noDashboardAccessRoles.includes(root?.roleType)
            ? !!value
            : true;
        }),

      locationServicesOptions: yup
        .array()
        .of(
          yup.object({
            type: yup.string().required("Required"),
            name: yup.string().required("Required"),
          }),
        ),

      services: yup
        .array()
        .of(yup.string().required("Required"))
        .test(
          "required-if-operator",
          "At least one service is required",
          function (value) {
            const root = this.from?.[this.from.length - 1]?.value;
            return noDashboardAccessRoles.includes(root?.roleType)
              ? (value?.length ?? 0) > 0
              : true;
          },
        ),
    }),
  ),
  // for zone manager
  scheduleStartDate: requiredForZoneManagerCity(yup.date().nullable()),
  scheduleEndDate: requiredForZoneManagerCity(yup.date().nullable()),
  scheduleStartTime: requiredForZoneManagerCity(yup.date().nullable()),
  scheduleEndTime: requiredForZoneManagerCity(yup.date().nullable()),
  scheduleFlexibleHours: yup.string().when(["roleType"], {
    is: (roleType: string) => roleType === "Zone Manager",

    then: (schema) =>
      schema.test("hours-range", function (value) {
        if (!value) return true; // optional

        const hours = Number(value);

        if (hours < 0) {
          return this.createError({ message: "Min is 0" });
        }

        if (hours > 3) {
          return this.createError({ message: "Max is 3" });
        }

        return true;
      }),

    otherwise: (schema) => schema.notRequired(),
  }),
  scheduleDays: yup
    .array()
    .of(yup.string().required("Required"))
    .when(["roleType"], {
      is: (roleType: string) => roleType === "Zone Manager",
      then: (schema) =>
        schema.required("Required").min(1, "Select at least one day"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

export const initAdminPermissions = [
  {
    name: "reports",
    label: "Reports",
    permissions: [
      {
        id: "viewSalesSummary",
        label: "Sales Summary",
        checked: false,
      },
      {
        id: "viewKafdSalesReport",
        label: "KAFD API Report",
        checked: false,
      },
    ],
  },
  {
    name: "businessIntelligence",
    label: "Business Intelligence",
    permissions: [
      {
        id: "viewTransactions",
        label: "Transactions",
        checked: false,
      },
    ],
  },
  {
    name: "locations",
    label: "Locations",
    permissions: [
      {
        id: "view",
        label: "View",
        checked: false,
      },
      {
        id: "create",
        label: "Create",
        checked: false,
      },
      {
        id: "edit",
        label: "Edit",
        checked: false,
      },
    ],
  },
  {
    name: "clients",
    label: "Clients",
    permissions: [
      {
        id: "view",
        label: "View",
        checked: false,
      },
      {
        id: "create",
        label: "Create",
        checked: false,
      },
      {
        id: "edit",
        label: "Edit",
        checked: false,
      },
      // not editable permission
      {
        id: "account_user_create",
        label: "accountUserCreate",
        checked: false,
      },
    ],
  },
  {
    name: "transactions",
    label: "Transactions",
    permissions: [
      {
        id: "view",
        label: "View",
        checked: false,
      },
      {
        id: "edit",
        label: "Edit",
        checked: false,
      },
      {
        id: "ticketValidation",
        label: "Tickets Validation",
        checked: false,
      },
      {
        id: "golfRequests",
        label: "Golf Carts Requests",
        checked: false,
      },
    ],
  },
  {
    name: "customers",
    label: "Customers",
    permissions: [
      {
        id: "viewCustomersList",
        label: "View Customers List",
        checked: false,
      },
      {
        id: "viewFeedback",
        label: "View Feedback",
        checked: false,
      },
    ],
  },
  {
    name: "arsannEmployees",
    label: "Arsann Employees",
    permissions: [
      {
        id: "view",
        label: "View",
        checked: false,
      },
      {
        id: "create",
        label: "Create",
        checked: false,
      },
      {
        id: "edit",
        label: "Edit",
        checked: false,
      },
    ],
  },
  {
    name: "delivery",
    label: "Arsann Delivery",
    permissions: [
      {
        id: "view",
        label: "View",
        checked: false,
      },
      {
        id: "create",
        label: "Create",
        checked: false,
      },
      {
        id: "edit",
        label: "Edit",
        checked: false,
      },
    ],
  },
];

export type Role =
  | "Super Admin"
  | "Admin"
  | "Location Manager"
  | "Zone Manager"
  | "Supervisor"
  | "Supervisor Assistant"
  | "Operator"
  | "HR"
  | "Finance"
  | "Operations Head"
  | "Driver"
  | "Key-holder"
  | "Cameraman";

const rolePermissionsMap: Record<Role, string[]> = {
  "Super Admin": initAdminPermissions.map((module) => module.name),
  Admin: initAdminPermissions.map((module) => module.name),
  HR: initAdminPermissions.map((module) => module.name),
  Finance: initAdminPermissions.map((module) => module.name),
  "Operations Head": initAdminPermissions.map((module) => module.name),

  "Location Manager": [
    "reports",
    "businessIntelligence",
    "locations",
    "clients",
    "transactions",
    "arsannEmployees",
    "delivery",
  ],

  "Zone Manager": [
    "reports",
    "businessIntelligence",
    "locations",
    "clients",
    "transactions",
    "arsannEmployees",
    "delivery",
  ],

  Supervisor: ["arsannEmployees", "delivery"],

  "Supervisor Assistant": ["arsannEmployees", "delivery"],

  Operator: [],
  Driver: [],
  "Key-holder": [],
  Cameraman: [],
};

export const getPermissionsByRole = (role: Role) => {
  const allowedModules = rolePermissionsMap[role] || [];

  return initAdminPermissions.filter((module) =>
    allowedModules.includes(module.name),
  );
};

export const daysList = [
  { label: "Sunday", value: "Sun" },
  { label: "Monday", value: "Mon" },
  { label: "Tuesday", value: "Tue" },
  { label: "Wednesday", value: "Wed" },
  { label: "Thursday", value: "Thu" },
  { label: "Friday", value: "Fri" },
  { label: "Saturday", value: "Sat" },
];

export type Shift = {
  shiftType?: string;
  startTime?: Date;
  endTime?: Date;
  days?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
};

type OverlapError = {
  locationName: string;
  shiftA: string;
  shiftB: string;
  days: string[];
};

type LocationSchedule = {
  locationId: string;
  locationName: string;
  shifts: Shift[];
};

function isOverlapping(startA: Date, endA: Date, startB: Date, endB: Date) {
  const aStart = moment(startA).format("HH:mm");
  const aEnd = moment(endA).format("HH:mm");
  const bStart = moment(startB).format("HH:mm");
  const bEnd = moment(endB).format("HH:mm");

  // Convert to minutes since midnight for easy comparison
  const [aS, aE, bS, bE] = [aStart, aEnd, bStart, bEnd].map((t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  });

  return aS < bE && bS < aE; // overlap if one starts before the other ends
}

export function validateShifts(locations: LocationSchedule[]) {
  const errors: OverlapError[] = [];

  locations.forEach((location) => {
    const { locationName, shifts } = location;

    // compare each shift with every other shift at same location
    for (let i = 0; i < shifts.length; i++) {
      for (let j = i + 1; j < shifts.length; j++) {
        const shiftA = shifts[i];
        const shiftB = shifts[j];

        const shiftADays = shiftA.days ?? [];
        const shiftBDays = shiftB.days ?? [];

        // check if they share any common day
        const commonDays = shiftADays.filter((d) => shiftBDays.includes(d));
        if (commonDays.length === 0) continue;

        if (
          shiftA.startTime &&
          shiftA.endTime &&
          shiftB.startTime &&
          shiftB.endTime &&
          isOverlapping(
            shiftA.startTime,
            shiftA.endTime,
            shiftB.startTime,
            shiftB.endTime,
          )
        ) {
          errors.push({
            locationName,
            shiftA: shiftA.shiftType ?? "",
            shiftB: shiftB.shiftType ?? "",
            days: commonDays,
          });
        }
      }
    }
  });

  return errors;
}

export const rolesList = [
  "Super Admin",
  "Admin",
  "HR",
  "Finance",
  "Operations Head",
  "Zone Manager",
  "Location Manager",
  "Supervisor",
  "Supervisor Assistant",
  "Operator",
  "Driver",
  "Key-holder",
  "Cameraman",
];

export const canManageRole = (
  currentUserRole?: string,
  targetUserRole?: string,
): boolean => {
  if (!currentUserRole || !targetUserRole) return false;

  // Super Admin can manage everyone
  if (currentUserRole === "Super Admin") return true;

  // These roles cannot manage anyone
  const restrictedRoles = new Set([
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
    "HR",
    "Finance",
  ]);

  if (restrictedRoles.has(currentUserRole)) {
    return false;
  }

  const currentRoleIndex = rolesList.indexOf(currentUserRole);
  const targetRoleIndex = rolesList.indexOf(targetUserRole);

  if (currentRoleIndex === -1 || targetRoleIndex === -1) {
    return false;
  }

  // Can manage only roles below their own
  return targetRoleIndex > currentRoleIndex;
};

export const ROLE_OPTIONS: Record<Role, Role[]> = {
  "Super Admin": [
    "Super Admin",
    "Admin",
    "HR",
    "Finance",
    "Operations Head",
    "Zone Manager",
    "Location Manager",
    "Supervisor",
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  Admin: [
    "HR",
    "Finance",
    "Operations Head",
    "Zone Manager",
    "Location Manager",
    "Supervisor",
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  HR: [],
  Finance: [],

  "Operations Head": [
    "Zone Manager",
    "Location Manager",
    "Supervisor",
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  "Zone Manager": [
    "Location Manager",
    "Supervisor",
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  "Location Manager": [
    "Supervisor",
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  Supervisor: [
    "Supervisor Assistant",
    "Operator",
    "Driver",
    "Key-holder",
    "Cameraman",
  ],

  "Supervisor Assistant": ["Operator", "Driver", "Key-holder", "Cameraman"],

  Operator: [],
  Driver: [],
  "Key-holder": [],
  Cameraman: [],
};

export const employeeRoleFlags = [
  "isSuperAdmin",
  "isArsannAdmin",
  "isHR",
  "isFinance",
  "isOperationsHead",
  "isLocationManager",
  "isZoneManager",
  "isSupervisor",
  "isSupervisorAssistant",
  "isOperator",
  "isKeyHolder",
  "isCameraman",
  "isDriver",
] as const;
